import { HttpErrorResponse } from '@angular/common/http'
import { Type, inject } from '@angular/core'

import { Actions, FunctionalEffect, createEffect, ofType } from '@ngrx/effects'
import { ActionCreator, Store } from '@ngrx/store'

import { catchError, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { DataModelReference } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { DataModelDeleteAttributeModalComponent } from '@flowx/designer/modules/data-model-builder/components/data-model-delete-attribute-modal/data-model-delete-attribute-modal.component'
import { DeleteEntityConfirmationModalComponent } from '@flowx/designer/modules/data-model-builder/components/delete-entity-confirmation-modal/delete-entity-confirmation-modal.component'
import { EditAttributeConfirmationModalComponent } from '@flowx/designer/modules/data-model-builder/components/edit-attribute-confirmation-modal/edit-attribute-confirmation-modal.component'
import { removesTypeSpecificValidators } from '@flowx/designer/modules/data-model-builder/services/attribute-type-change.utils'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'
import {
  ResourceEntityDataModelToUIEntity,
  UIEntityToDataModelEntity,
  expandEntityPathsAndReferences,
} from '@flowx/designer/shared/entities/data-model.entity'
import {
  selectCurrentResourceId,
  selectResourceDataModelEntity,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { IError } from '../../api/api-http.service'
import { DataModelApiService } from '../../api/data-model.api'
import { ToastMessageService } from '../../services/toast-message.service'

interface DataModelEffectsActions {
  load: ActionCreator<string, any>
  loadSuccess: ActionCreator<string, any>
  loadFailure: ActionCreator<string, any>
  add: ActionCreator<string, any>
  addSuccess: ActionCreator<string, any> // This can trigger setNewEntityAsAttribute
  addFailure: ActionCreator<string, any>
  edit: ActionCreator<string, any>
  editSuccess: ActionCreator<string, any>
  editFailure: ActionCreator<string, any>
  addAttribute: ActionCreator<string, any>
  addAttributeSuccess: ActionCreator<string, any>
  addAttributeFailure: ActionCreator<string, any>
  editAttribute: ActionCreator<string, any> // This can be triggered by setNewEntityAsAttribute
  editAttributeSuccess: ActionCreator<string, any>
  editAttributeFailure: ActionCreator<string, any>
  requestDeleteEntity: ActionCreator<string, any>
  deleteEntity: ActionCreator<string, any>
  deleteEntitySuccess: ActionCreator<string, any>
  deleteEntityFailure: ActionCreator<string, any>
  requestDeleteAttribute: ActionCreator<string, any>
  deleteAttribute: ActionCreator<string, any>
  deleteAttributeSuccess: ActionCreator<string, any>
  deleteAttributeFailure: ActionCreator<string, any>
  resetErrors: ActionCreator<string, any>
  resetErrorsSuccess: ActionCreator<string, any>
  updateAttributeValidators: ActionCreator<string, any>
  updateAttributeValidatorsSuccess: ActionCreator<string, any>
  updateAttributeValidatorsFailure: ActionCreator<string, any>
}

export const createDataModelEffects = (
  resourceType: ApplicationResourceType,
  actions: DataModelEffectsActions,
  apiService: Type<DataModelApiService>
): {
  loadEntities$: FunctionalEffect
  addEntity$: FunctionalEffect
  editEntity$: FunctionalEffect
  addAttribute$: FunctionalEffect
  editAttribute$: FunctionalEffect
  setNewEntityAsAttribute$: FunctionalEffect
  startDeleteEntity$: FunctionalEffect
  deleteEntity$: FunctionalEffect
  startDeleteAttribute$: FunctionalEffect
  deleteAttribute$: FunctionalEffect
  resetErrors$: FunctionalEffect
  updateAttributeValidators$: FunctionalEffect
} => {
  const loadEntities$ = createEffect(
    (actions$ = inject(Actions), api = inject(apiService), toast = inject(ToastMessageService)) =>
      actions$.pipe(
        ofType(actions.load),
        switchMap(({ resourceId }) =>
          api.getDataModel({ resourceType, resourceId }).pipe(
            map((dataModel) =>
              actions.loadSuccess({
                resourceId,
                entities: mapResource(dataModel).dataEntities.map((entity) =>
                  ResourceEntityDataModelToUIEntity(entity, resourceType)
                ),
              })
            ),
            catchError(() => {
              toast.showErrorMessage('Load data model types failed')
              return of(actions.loadFailure({ resourceId }))
            })
          )
        )
      ),
    { functional: true }
  )

  const addEntity$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toast = inject(ToastMessageService)
    ) =>
      actions$.pipe(
        ofType(actions.add),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entity, correlationAttribute }, resourceId]) =>
          api.addDataModelEntity({ resourceType, resourceId }, entity).pipe(
            map((entity) =>
              actions.addSuccess({
                resourceId,
                entity: ResourceEntityDataModelToUIEntity(mapResource(entity), resourceType),
                correlationAttribute,
              })
            ),
            tap(() => {
              toast.showSuccessMessage('Data model type added successfully')
            }),
            catchError(({ error }) => {
              toast.showErrorMessage('Add data model type failed', error.title)
              return of(actions.addFailure({ resourceId }))
            })
          )
        )
      ),
    { functional: true }
  )

  const editEntity$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService)
    ) =>
      actions$.pipe(
        ofType(actions.edit),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entity }, resourceId]) =>
          api
            .updateDataModelEntity({ resourceType, resourceId }, UIEntityToDataModelEntity(entity))
            .pipe(
              map((entity) =>
                actions.editSuccess({
                  resourceId,
                  entity: ResourceEntityDataModelToUIEntity(mapResource(entity), resourceType),
                })
              ),
              catchError(({ error }: { error: IError }) => {
                toastMessageService.showErrorMessage('Edit data model type failed', error.title)
                return of(
                  actions.editFailure({
                    resourceId,
                    entityId: entity.identifier,
                    error,
                  })
                )
              })
            )
        )
      ),
    { functional: true }
  )

  const addAttribute$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService)
    ) =>
      actions$.pipe(
        ofType(actions.addAttribute),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entityId, attribute }, resourceId]) =>
          api.addAttributeToDataModelEntity({ resourceType, resourceId }, entityId, attribute).pipe(
            map((entity) => mapResource(entity)),
            map((attribute) =>
              actions.addAttributeSuccess({
                resourceId,
                entityId,
                attribute,
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage('Add data model attribute failed', error.title)
              return of(actions.addAttributeFailure({ resourceId, entityId }))
            })
          )
        )
      ),
    {
      functional: true,
    }
  )

  const editAttribute$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService),
      flxModalService = inject(FlxModalService)
    ) =>
      actions$.pipe(
        ofType(actions.editAttribute),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entityId, attribute, force }, resourceId]) =>
          store.select(selectResourceDataModelEntity(entityId)).pipe(
            take(1),
            switchMap((entity) => {
              const currentAttribute = entity?.attributes?.find((a) => a.id === attribute.id)
              if (force || !removesTypeSpecificValidators(currentAttribute, attribute)) {
                return of(true)
              }
              return flxModalService
                .show(
                  ConfirmationModalComponent,
                  { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
                  {
                    title: `Are you sure you want to change the type of ${currentAttribute?.name}?`,
                    message:
                      'All type-specific validators will be removed and must be reconfigured.',
                    btnOkText: 'Change type',
                  }
                )
                .data$.pipe(
                  take(1),
                  filter((result) => result?.confirm === true),
                  map(() => true)
                )
            }),
            switchMap(() =>
              api
                .updateAttributeInDataModelEntity(
                  { resourceType, resourceId },
                  entityId,
                  attribute,
                  force
                )
                .pipe(
                  map((entity) => mapResource(entity)),
                  map((attribute) =>
                    actions.editAttributeSuccess({
                      resourceId,
                      entityId,
                      attribute,
                    })
                  ),
                  tap(() => flxModalService.hide()),
                  catchError((err: HttpErrorResponse) => {
                    const { error }: { error: IError & { references: DataModelReference[] } } = err
                    if (err.status === 409) {
                      return flxModalService
                        .show(
                          EditAttributeConfirmationModalComponent,
                          {
                            closeOnBackdropClick: true,
                            backdropClass: 'overlay-backdrop',
                            panelClass: 'max-h-[80%]',
                          },
                          {
                            attribute,
                            references: error?.references,
                            resourceType,
                          }
                        )
                        .data$.pipe(
                          map(() =>
                            actions.editAttribute({
                              entityId,
                              attribute,
                              force: true,
                            })
                          )
                        )
                    } else {
                      toastMessageService.showErrorMessage(
                        'Edit data model attribute failed',
                        error.title
                      )
                      return of(
                        actions.editAttributeFailure({
                          resourceId,
                          entityId,
                          attributeId: attribute.id,
                          error,
                        })
                      )
                    }
                  })
                )
            )
          )
        )
      ),
    {
      functional: true,
    }
  )

  const setNewEntityAsAttribute$ = createEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(actions.addSuccess),
        filter(({ correlationAttribute }) => !!correlationAttribute),
        switchMap(({ entity, correlationAttribute }) => {
          if (correlationAttribute.attribute.id) {
            return of(
              actions.editAttribute({
                entityId: correlationAttribute.from,
                attribute: {
                  ...correlationAttribute.attribute,
                  isSensitive: null,
                  useInReporting: null,
                  reference: { targetFlowxUuid: entity.flowxUuid },
                },
                force: false,
              })
            )
          } else {
            return of(
              actions.addAttribute({
                entityId: correlationAttribute.from,
                attribute: {
                  ...correlationAttribute.attribute,
                  reference: { targetFlowxUuid: entity.flowxUuid },
                },
              })
            )
          }
        })
      ),
    {
      functional: true,
    }
  )

  const startDeleteEntity$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      flxModalService = inject(FlxModalService)
    ) =>
      actions$.pipe(
        ofType(actions.requestDeleteEntity),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entity }, resourceId]) =>
          api.getReferencesByEntityId({ resourceId, resourceType }, entity.flowxUuid).pipe(
            map((response) => expandEntityPathsAndReferences(mapResource(response))),
            switchMap((references) =>
              flxModalService
                .show(
                  DeleteEntityConfirmationModalComponent,
                  {
                    closeOnBackdropClick: true,
                    backdropClass: 'overlay-backdrop',
                    panelClass: 'max-h-[80%]',
                  },
                  {
                    entity,
                    references,
                    resourceType,
                  }
                )
                .data$.pipe(map(() => actions.deleteEntity({ entityId: entity.identifier })))
            )
          )
        )
      ),
    {
      functional: true,
    }
  )

  const deleteEntity$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService),
      flxModalService = inject(FlxModalService)
    ) =>
      actions$.pipe(
        ofType(actions.deleteEntity),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entityId }, resourceId]) =>
          api.deleteEntity({ resourceType, resourceId }, entityId).pipe(
            tap(() => {
              flxModalService.hide()
              toastMessageService.showSuccessMessage('Data model type deleted successfully')
            }),
            map((resp) =>
              actions.deleteEntitySuccess({
                resourceId,
                entityId,
                affectedEntities: mapResource(resp),
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage('Delete data model type failed', error.title)
              return of(
                actions.deleteEntityFailure({
                  resourceId,
                  entityId,
                })
              )
            })
          )
        )
      ),
    {
      functional: true,
    }
  )

  const startDeleteAttribute$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      flxModalService = inject(FlxModalService)
    ) =>
      actions$.pipe(
        ofType(actions.requestDeleteAttribute),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entity, attribute, path }, resourceId]) =>
          flxModalService
            .show(
              DataModelDeleteAttributeModalComponent,
              {
                closeOnBackdropClick: true,
                backdropClass: 'overlay-backdrop',
              },
              {
                entity,
                attribute,
                path,
                resourceId,
                resourceType,
              }
            )
            .data$.pipe(
              map(() =>
                actions.deleteAttribute({
                  entityId: entity.identifier,
                  attribute,
                })
              )
            )
        )
      ),
    {
      functional: true,
    }
  )

  const deleteAttribute$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService),
      flxModalService = inject(FlxModalService)
    ) =>
      actions$.pipe(
        ofType(actions.deleteAttribute),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entityId, attribute }, resourceId]) =>
          api
            .deleteAttributeInDataModelEntity({ resourceType, resourceId }, entityId, attribute.id)
            .pipe(
              tap(() => {
                flxModalService.hide()
                toastMessageService.showSuccessMessage('Attribute deleted successfully')
              }),
              map(() =>
                actions.deleteAttributeSuccess({
                  resourceId,
                  entityId,
                  attribute,
                })
              ),
              catchError(({ error }: { error: IError }) => {
                toastMessageService.showErrorMessage('Delete attribute failed', error.title)
                return of(
                  actions.deleteAttributeFailure({
                    resourceId,
                    entityId,
                    attributeId: attribute.id,
                  })
                )
              })
            )
        )
      ),
    {
      functional: true,
    }
  )

  const resetErrors$ = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(actions.resetErrors),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ id }, resourceId]) => of(actions.resetErrorsSuccess({ resourceId, id })))
      ),
    {
      functional: true,
    }
  )

  const updateAttributeValidators$ = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      api = inject(apiService),
      toastMessageService = inject(ToastMessageService)
    ) =>
      actions$.pipe(
        ofType(actions.updateAttributeValidators),
        withLatestFrom(store.select(selectCurrentResourceId)),
        switchMap(([{ entityId, attributeId, attributeFlowxUuid, validators }, resourceId]) =>
          api
            .updateAttributeValidators(
              { resourceType, resourceId },
              entityId,
              attributeFlowxUuid,
              validators
            )
            .pipe(
              map(() =>
                actions.updateAttributeValidatorsSuccess({
                  resourceId,
                  entityId,
                  attributeId,
                  validators,
                })
              ),
              catchError(({ error }: { error: IError }) => {
                toastMessageService.showErrorMessage(
                  'Update data model attribute validations failed',
                  error?.title
                )
                return of(
                  actions.updateAttributeValidatorsFailure({
                    resourceId,
                    entityId,
                    attributeId,
                  })
                )
              })
            )
        )
      ),
    {
      functional: true,
    }
  )

  return {
    loadEntities$,
    addEntity$,
    editEntity$,
    addAttribute$,
    editAttribute$,
    setNewEntityAsAttribute$,
    startDeleteEntity$,
    deleteEntity$,
    startDeleteAttribute$,
    deleteAttribute$,
    resetErrors$,
    updateAttributeValidators$,
  }
}
